import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useAppStore from '../store'; // Default Import für Zustand-Store
import { formatMoney } from '../utils/currencyFormatter';

const PEWEB_LOGO_SLOGAN = '/assets/PEWeb/peweb-logo-slogan.png';
const PEWEB_LOGO = '/assets/PEWeb/peweb-logo.png';
const PEWEB_NEWSLETTER_HERO = '/assets/PEWeb/peweb-newsletter-hero.png';
const PEWEB_BASIC_LAPTOP = '/assets/PEWeb/basic-laptop.png';
const PEWEB_BASIC_PHONE = '/assets/PEWeb/basic-phone.png';
const PEWEB_INDIVIDUAL_LAPTOP = '/assets/PEWeb/individual-laptop.png';
const PEWEB_INDIVIDUAL_PHONE = '/assets/PEWeb/individual-phone.png';
const PEWEB_ADDON_SEO = '/assets/PEWeb/addon-seo.png';
const PEWEB_ADDON_PRINT = '/assets/PEWeb/addon-print.png';

// --- HILFSKOMPONENTEN (UI) ---

// Resultat-Feld (Nur Anzeige) — WebDesign ist PE-Produkt, Beträge strikt EUR
const CalculatedField = ({ value }: { value: number }) => {
  const displayValue = formatMoney(value, 'EUR', 2);

  return (
    <div className="flex items-center justify-center bg-white/80 dark:bg-[#08090b]/80 border border-[#8D6435]/40 dark:border-[#E6C299]/40 rounded-lg w-full p-2 text-center text-pe-accent-text dark:text-[#E6C299] font-bold font-mono cursor-default h-[42px] shadow-[inset_0_0_12px_rgba(230,194,153,0.08)]">
      <span>{displayValue}</span>
    </div>
  );
};

const MAX_ZUSATZ_SEITEN = 20;

/** Sichtbarer PE-Haken (native ::after auf input ist unzuverlässig). */
const PeCheck: React.FC<{
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}> = ({ id, checked, onChange, disabled }) => (
  <label
    htmlFor={id}
    className={`relative inline-flex h-5 w-5 flex-shrink-0 items-center justify-center ${
      disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
    }`}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="peer sr-only"
    />
    <span
      aria-hidden
      className={`flex h-5 w-5 items-center justify-center rounded border text-[11px] font-black leading-none transition-colors ${
        checked
          ? 'border-[#E6C299] bg-[#E6C299] text-black'
          : 'border-pe-border-strong dark:border-[#2a2a2a] bg-white dark:bg-[#08090b] text-transparent'
      }`}
    >
      ✓
    </span>
  </label>
);

/** Seitenanzahl mit ± statt Dropdown (Standard 1). */
const SeitenStepper: React.FC<{
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
}> = ({ value, onChange, min = 1, max = MAX_ZUSATZ_SEITEN }) => (
  <div className="inline-flex flex-shrink-0 items-center rounded-lg border border-[#8D6435] dark:border-[#E6C299] bg-pe-input dark:bg-[#08090b] overflow-hidden">
    <button
      type="button"
      aria-label="Eine Seite weniger"
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
      className="h-10 w-9 text-lg font-bold text-pe-accent-text dark:text-[#E6C299] hover:bg-pe-hover dark:hover:bg-[#0f1015] disabled:opacity-30 disabled:hover:bg-transparent"
    >
      −
    </button>
    <span className="min-w-[2.75rem] px-1 text-center text-sm font-bold tabular-nums text-pe-accent-text dark:text-[#E6C299]">
      {value}
    </span>
    <button
      type="button"
      aria-label="Eine Seite mehr"
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + 1))}
      className="h-10 w-9 text-lg font-bold text-pe-accent-text dark:text-[#E6C299] hover:bg-pe-hover dark:hover:bg-[#0f1015] disabled:opacity-30 disabled:hover:bg-transparent"
    >
      +
    </button>
  </div>
);

/** CSS Laptop-/Phone-Mockup mit Screen-Assets */
const DeviceShowcase = ({
  variant,
  size = 'md',
}: {
  variant: 'basic' | 'individual';
  size?: 'md' | 'lg';
}) => {
  const isBasic = variant === 'basic';
  const tall = size === 'lg';
  const laptopSrc = isBasic ? PEWEB_BASIC_LAPTOP : PEWEB_INDIVIDUAL_LAPTOP;
  const phoneSrc = isBasic ? PEWEB_BASIC_PHONE : PEWEB_INDIVIDUAL_PHONE;
  return (
    <div
      className={`relative w-full flex items-end justify-center gap-3 select-none pointer-events-none ${
        tall ? 'h-56 sm:h-64' : 'h-44'
      }`}
    >
      {/* Laptop */}
      <div className={`relative w-[72%] ${tall ? 'max-w-[320px]' : 'max-w-[260px]'}`}>
        <div
          className={`rounded-t-lg border ${
            isBasic ? 'border-slate-600 bg-slate-800/80' : 'border-[#E6C299]/50 bg-[#15171e]'
          } aspect-[16/10] p-1.5 shadow-[0_0_24px_rgba(230,194,153,0.12)]`}
        >
          <div className="h-full w-full rounded-sm overflow-hidden bg-black">
            <img
              src={laptopSrc}
              alt={isBasic ? 'PEWeb Basic Desktop' : 'PEWeb Individual Desktop'}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
        <div className="h-1.5 w-[108%] -ml-[4%] rounded-b-md bg-slate-700 border border-slate-600" />
        <div className="h-0.5 w-[40%] mx-auto mt-0.5 rounded bg-slate-600" />
      </div>
      {/* Phone */}
      <div
        className={`relative rounded-xl border-2 overflow-hidden ${
          isBasic ? 'border-slate-500 bg-slate-800' : 'border-[#E6C299]/60 bg-[#0f1015]'
        } shadow-lg -mb-1 ${tall ? 'w-14 h-[120px] sm:w-16 sm:h-[136px]' : 'w-12 h-[100px]'}`}
      >
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-slate-600 z-10" />
        <img
          src={phoneSrc}
          alt={isBasic ? 'PEWeb Basic Mobile' : 'PEWeb Individual Mobile'}
          className="w-full h-full object-cover object-top"
        />
      </div>
    </div>
  );
};

/** Add-on Showcase: SEO + Print Assets in CSS-Rahmen */
const AddonShowcase = () => (
  <div className="relative w-full h-36 flex items-center justify-center gap-4 select-none pointer-events-none">
    {/* SEO */}
    <div className="relative w-[42%] max-w-[130px] rounded-xl border border-[#E6C299]/40 bg-gradient-to-br from-[#1a1612] to-[#08090b] p-1.5 shadow-[0_0_24px_rgba(230,194,153,0.14)] overflow-hidden">
      <div className="aspect-[4/3] w-full rounded-lg overflow-hidden bg-black">
        <img
          src={PEWEB_ADDON_SEO}
          alt="PEWeb SEO Add-on"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <p className="mt-1.5 text-[8px] font-bold uppercase tracking-wider text-[#8D6435]/80 dark:text-[#E6C299]/80 text-center">
        SEO
      </p>
    </div>
    {/* Print / CI */}
    <div className="relative w-[38%] max-w-[110px]">
      <div className="absolute inset-x-1 -top-1 h-full rounded-lg border border-slate-600 bg-slate-800/80 rotate-[-4deg]" />
      <div className="absolute inset-x-0.5 top-0 h-full rounded-lg border border-slate-500 bg-slate-700/90 rotate-[3deg]" />
      <div className="relative rounded-lg border border-[#E6C299]/45 bg-[#15171e] p-1.5 shadow-[0_0_20px_rgba(230,194,153,0.1)] overflow-hidden">
        <div className="aspect-[3/4] w-full rounded-md overflow-hidden bg-black">
          <img
            src={PEWEB_ADDON_PRINT}
            alt="PEWeb Print Add-on"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <p className="mt-1.5 text-[8px] font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400 text-center">
          Print
        </p>
      </div>
    </div>
  </div>
);

const AccordionItem = ({
  id,
  openId,
  setOpenId,
  title,
  children,
}: {
  id: string;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  title: string;
  children: React.ReactNode;
}) => {
  const open = openId === id;
  return (
    <div className="rounded-xl border border-pe-border dark:border-slate-800/80 bg-white/80 dark:bg-[#0c0d11]/80 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors"
      >
        <span className="text-base font-bold text-pe-accent-text dark:text-[#E6C299] tracking-wide">{title}</span>
        <span className={`text-slate-500 text-lg leading-none transition-transform ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
      {open ? (
        <div className="px-4 pb-4 text-base text-pe-muted dark:text-slate-400 leading-relaxed border-t border-pe-border dark:border-slate-800/60 pt-3 space-y-2">
          {children}
        </div>
      ) : null}
    </div>
  );
};

// --- HAUPTKOMPONENTE ---

const WebDesignPage = () => {
  // 1. STORE ANBINDUNG
  const settings = useAppStore((state) => state.settings as any);
  const countrySettings = settings?.country;
  
  // WebDesign / PE-Leasing: Preise immer EUR (Billing-Lock), nie Salon-CHF

  // Mehrwertsteuer
  const globalMwSt = countrySettings?.MwSt_Satz ?? 19;

  // --- REVISION V3 (FINAL FIX) ---
  // Wir definieren storePackages NICHT mehr hier oben, sondern direkt im useMemo.
  // Das verhindert die "logical expression"-Warnung.

  // 2. DATEN-AUFBEREITUNG (baseData)
  const baseData = useMemo(() => {
    // FIX: Variable hierhin verschoben. 
    // Zugriff erfolgt direkt auf 'settings', was im Dependency-Array steht.
    const storePackages = settings?.webdesignPackages || {};

    return {
      // BASIC
      basicMonat12: storePackages.basic_mon_12 || 85.00,
      basicGesamt12: storePackages.basic_gesamt_12 || 1020.00,
      basicZusatzSeite12: storePackages.basic_zs_12 || 8.00,
      
      basicMonat24: storePackages.basic_mon_24 || 50.00,
      basicGesamt24: storePackages.basic_gesamt_24 || 1200.00,
      basicZusatzSeite24: storePackages.basic_zs_24 || 4.00,
      
      kaufpreisBasic: storePackages.basic_kauf || 1950.00,

      // INDIVIDUELL
      individuellMonat12: storePackages.indiv_mon_12 || 162.40,
      individuellGesamt12: storePackages.indiv_gesamt_12 || 1950.00,
      individuellZusatzSeite12: storePackages.indiv_zs_12 || 12.50,
      
      individuellMonat24: storePackages.indiv_mon_24 || 89.50,
      individuellGesamt24: storePackages.indiv_gesamt_24 || 2148.00,
      individuellZusatzSeite24: storePackages.indiv_zs_24 || 6.25,

      kaufpreisIndividual: storePackages.indiv_kauf || 3650.00,
      
      // SEO
      seoMonat12: storePackages.seo_monatl_12 || 16.50,
      seoMonat24: storePackages.seo_monatl_24 || 8.25,
      seoGesamt: storePackages.seo_gesamt || 198.00,
      
      // BILDER
      basicImageUrl: storePackages.basicImageUrl || "https://i.imgur.com/LoNDknO.png",
      individualImageUrl: storePackages.individualImageUrl || "https://i.imgur.com/Cfg8Kpv.png",
      zusatzImageUrl: storePackages.zusatzImageUrl || "https://i.imgur.com/pzxEKJH.png"
    };
  }, [settings]); // Abhängigkeit ist jetzt nur noch das settings-Objekt selbst.

  // User-Auswahl State
  const [selectedOptions, setSelectedOptions] = useState<{
    paket: 'basic' | 'individual' | null;
    laufzeit: 12 | 24 | null;
    zusatzSeitenChecked: boolean;
    anzahlZusatzSeiten: number;
    seoChecked: boolean;
  }>({
    paket: null,
    laufzeit: null,
    zusatzSeitenChecked: false,
    anzahlZusatzSeiten: 0,
    seoChecked: false
  });

  // Logik-Schalter
  const [isKauf, setIsKauf] = useState(false);

  // UI State: Standard-Zoom auf 100%
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  // Listener
  useEffect(() => {
    const handleScroll = () => { setShowScrollButtons(window.scrollY > 100); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handler
  const handlePaketChange = (paket: 'basic' | 'individual') => setSelectedOptions(prev => ({ ...prev, paket }));
  
  const handleLaufzeitChange = (laufzeit: 12 | 24) => {
    if (!isKauf) {
        setSelectedOptions(prev => ({ ...prev, laufzeit }));
    }
  };
  
  const handleZusatzSeitenCheck = (checked: boolean) => {
    setSelectedOptions(prev => ({
      ...prev,
      zusatzSeitenChecked: checked,
      anzahlZusatzSeiten: checked ? (prev.anzahlZusatzSeiten > 0 ? prev.anzahlZusatzSeiten : 1) : 0
    }));
  };

  const handleAnzahlZusatzSeitenChange = (next: number) => {
    setSelectedOptions((prev) => ({
      ...prev,
      anzahlZusatzSeiten: Math.max(1, Math.min(MAX_ZUSATZ_SEITEN, next)),
    }));
  };

  const handleSeoCheck = (checked: boolean) => setSelectedOptions(prev => ({ ...prev, seoChecked: checked }));

  const handleKaufCheck = (checked: boolean) => {
    setIsKauf(checked);
    if (checked) {
        setSelectedOptions(prev => ({ ...prev, laufzeit: null }));
    }
  };

  // Zoom Handler
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 10, 150));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 10, 50));
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const scrollToBottom = () => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });

  // Hilfsfunktion: WebDesign-Preise strikt EUR (PE-Billing)
  const formatCurrencyLocal = useCallback((value: number) => formatMoney(value, 'EUR', 2), []);

  // KERN-LOGIK: Preisberechnung
  const calculation = useMemo(() => {
    let netto = 0;
    let grundpreisDisplay = 0;
    let seoPreisDisplay = 0;
    let seitenPreisDisplay = 0;

    const mwstSatz = globalMwSt;
    const p = selectedOptions.paket;

    if (p) {
        if (isKauf) {
            grundpreisDisplay = p === 'basic' ? baseData.kaufpreisBasic : baseData.kaufpreisIndividual;
            netto += grundpreisDisplay;

            if (selectedOptions.zusatzSeitenChecked && selectedOptions.anzahlZusatzSeiten > 0) {
                const rate24 = p === 'basic' ? baseData.basicZusatzSeite24 : baseData.individuellZusatzSeite24;
                const einzelPreisKauf = rate24 * 24; 
                seitenPreisDisplay = einzelPreisKauf * selectedOptions.anzahlZusatzSeiten;
                netto += seitenPreisDisplay;
            }

            if (selectedOptions.seoChecked) {
                seoPreisDisplay = baseData.seoGesamt;
                netto += seoPreisDisplay;
            }

        } else if (selectedOptions.laufzeit) {
            const lz = selectedOptions.laufzeit;
            grundpreisDisplay = p === 'basic' 
                ? (lz === 12 ? baseData.basicMonat12 : baseData.basicMonat24)
                : (lz === 12 ? baseData.individuellMonat12 : baseData.individuellMonat24);
            netto += grundpreisDisplay;

            if (selectedOptions.zusatzSeitenChecked && selectedOptions.anzahlZusatzSeiten > 0) {
                const seitenRate = p === 'basic'
                    ? (lz === 12 ? baseData.basicZusatzSeite12 : baseData.basicZusatzSeite24)
                    : (lz === 12 ? baseData.individuellZusatzSeite12 : baseData.individuellZusatzSeite24);
                seitenPreisDisplay = seitenRate * selectedOptions.anzahlZusatzSeiten;
                netto += seitenPreisDisplay;
            }

            if (selectedOptions.seoChecked) {
                seoPreisDisplay = lz === 12 ? baseData.seoMonat12 : baseData.seoMonat24;
                netto += seoPreisDisplay;
            }
        }
    }

    const brutto = netto * (1 + mwstSatz / 100);

    const btnDisplay = {
        basic: {
            leasing: `ab ${formatCurrencyLocal(Math.min(baseData.basicMonat12, baseData.basicMonat24))} / Monat`,
            kauf: `${formatCurrencyLocal(baseData.kaufpreisBasic)} (Kauf)`
        },
        individual: {
            leasing: `ab ${formatCurrencyLocal(Math.min(baseData.individuellMonat12, baseData.individuellMonat24))} / Monat`,
            kauf: `${formatCurrencyLocal(baseData.kaufpreisIndividual)} (Kauf)`
        },
        basic12: formatCurrencyLocal(baseData.basicMonat12),
        basic24: formatCurrencyLocal(baseData.basicMonat24),
        indi12: formatCurrencyLocal(baseData.individuellMonat12),
        indi24: formatCurrencyLocal(baseData.individuellMonat24)
    };

    return { 
        netto, 
        brutto, 
        grundpreisDisplay, 
        seoPreisDisplay, 
        seitenPreisDisplay,
        btnDisplay
    };
  }, [selectedOptions, baseData, isKauf, globalMwSt, formatCurrencyLocal]);

  const isStep2Enabled = selectedOptions.paket !== null; 
  const isStep3Enabled = isKauf ? isStep2Enabled : (selectedOptions.paket !== null && selectedOptions.laufzeit !== null);

  const features = [
    { title: 'Beauty-Branche', text: 'Designs & Sprache, die Salons verkaufen — nicht generische Templates.' },
    { title: 'Responsive High-End', text: 'Laptop bis Smartphone: Deine Marke wirkt überall premium.' },
    { title: 'SEO & KI-Konformität', text: 'Struktur, Keywords und Bildtexte für bessere Sichtbarkeit.' },
    { title: 'Flexible Laufzeiten', text: '12 oder 24 Monate Leasing — oder Kauf. Du entscheidest.' },
  ];

  return (
    <div className="bg-pe-bg dark:bg-[#08090b] text-pe-text dark:text-slate-200 font-sans min-h-screen w-full relative overflow-x-hidden">
      {/* Ambient gold glow */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] opacity-40"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(230,194,153,0.18), transparent 70%)',
        }}
      />

      {/* Zoom Controls */}
      <div className="hidden md:flex flex-col items-center fixed top-1/2 right-4 -translate-y-1/2 bg-white/90 dark:bg-[#15171e]/90 border border-pe-border dark:border-[#2a2a2a] p-1 rounded-lg shadow-lg space-y-1 z-50 backdrop-blur-sm">
        <button onClick={handleZoomIn} className="w-7 h-7 rounded-full bg-stone-200 dark:bg-[#1a1d24] hover:bg-stone-300 dark:hover:bg-[#22252e] text-pe-text dark:text-white font-bold text-lg flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#E6C299]">+</button>
        <span className="font-bold text-pe-accent-text dark:text-[#E6C299] text-xs tabular-nums py-1">{zoomLevel}%</span>
        <button onClick={handleZoomOut} className="w-7 h-7 rounded-full bg-stone-200 dark:bg-[#1a1d24] hover:bg-stone-300 dark:hover:bg-[#22252e] text-pe-text dark:text-white font-bold text-lg flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#E6C299]">-</button>
      </div>

      {/* Scroll Controls */}
      <div className={`fixed bottom-4 left-4 flex flex-col space-y-2 z-50 transition-opacity duration-300 ${showScrollButtons ? 'opacity-80 hover:opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <button onClick={scrollToTop} className="w-10 h-10 rounded-full bg-white/90 dark:bg-[#15171e]/90 border border-pe-border dark:border-[#2a2a2a] hover:bg-[#1a1d24] text-pe-accent-text dark:text-[#E6C299] flex items-center justify-center shadow-lg backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#E6C299]">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
        </button>
        <button onClick={scrollToBottom} className="w-10 h-10 rounded-full bg-white/90 dark:bg-[#15171e]/90 border border-pe-border dark:border-[#2a2a2a] hover:bg-[#1a1d24] text-pe-accent-text dark:text-[#E6C299] flex items-center justify-center shadow-lg backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#E6C299]">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
        </button>
      </div>

      <div className="relative w-full max-w-[98%] mx-auto pt-6 px-4 pb-12 transition-transform duration-300 ease-in-out" style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center' }}>

        {/* HERO — Desktop nebeneinander (Logo/Text | Motiv), Mobile gestapelt */}
        <header className="mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
            <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
              <img
                src={PEWEB_LOGO_SLOGAN}
                alt="PEWeb — PreisExperte Webdesign"
                className="h-24 sm:h-32 md:h-40 lg:h-44 w-auto max-w-[min(100%,420px)] object-contain drop-shadow-[0_0_28px_rgba(230,194,153,0.25)]"
              />
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.35em] text-[#8D6435]/80 dark:text-[#E6C299]/80">
                [pi:web] · Premium Websites für Beauty
              </p>
              <p className="mt-4 max-w-md text-pe-muted dark:text-slate-400 text-base sm:text-lg leading-relaxed">
                Dein digitaler Salon — so edel wie Dein Handwerk. PEWeb bringt Marke, Buchung und Vertrauen auf eine Seite.
              </p>
            </div>
            <div className="rounded-2xl border border-[#8D6435]/35 dark:border-[#E6C299]/35 overflow-hidden shadow-[0_12px_40px_rgba(28,25,23,0.12)] dark:shadow-[0_0_36px_rgba(230,194,153,0.12)] w-full max-w-[720px] mx-auto lg:max-w-none">
              <img
                src={PEWEB_NEWSLETTER_HERO}
                alt="Deine Website vergrault Kunden? — PEWeb Webdesign, das Stühle füllt"
                className="w-full h-auto object-contain block"
              />
            </div>
          </div>
        </header>

        {/* Feature Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-xl border border-pe-border dark:border-slate-800/80 bg-white/70 dark:bg-[#0f1015]/70 backdrop-blur-md p-4 shadow-[0_0_0_1px_rgba(230,194,153,0.06)] hover:border-[#E6C299]/35 transition-colors"
            >
              <h3 className="text-sm font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] mb-1.5">{f.title}</h3>
              <p className="text-sm text-pe-muted dark:text-slate-400 leading-snug">{f.text}</p>
            </div>
          ))}
        </div>

        {/* Paketübersicht — kompakt + Accordions für Details */}
        <div className="mb-10">
            <h2 className="text-2xl font-bold mb-6 text-center text-pe-text dark:text-slate-100 tracking-wide">
              PEWeb Pakete im Überblick
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
              {/* Basic */}
              <div className="rounded-2xl border border-pe-border-strong dark:border-slate-700/80 bg-gradient-to-b from-stone-100/95 to-white dark:from-[#15171e]/90 dark:to-[#0c0d11] backdrop-blur-sm p-5 flex flex-col shadow-[0_10px_30px_rgba(28,25,23,0.08)] dark:shadow-[0_0_30px_rgba(0,0,0,0.25)] hover:border-[#E6C299]/40 transition-colors">
                <h3 className="text-xl font-bold text-center text-pe-text dark:text-white mb-2">Basic</h3>
                <p className="text-sm text-pe-muted dark:text-slate-400 text-center leading-relaxed mb-4">
                  Strukturiert &amp; schnell live — WordPress-Themes, klarer Aufbau, starker Einstieg.
                </p>
                <DeviceShowcase variant="basic" size="lg" />
                <ul className="mt-4 text-sm text-pe-muted dark:text-slate-400 space-y-1.5 flex-grow">
                  <li>· WordPress Themes, anpassbar</li>
                  <li>· 5 Seiten inkl. Impressum &amp; Datenschutz</li>
                  <li>· Start · Team · Salon · Leistungen · Preise</li>
                </ul>
                <p className="mt-4 text-center text-sm font-mono text-[#8D6435]/90 dark:text-[#E6C299]/90">
                  ab {formatCurrencyLocal(Math.min(baseData.basicMonat12, baseData.basicMonat24))} / Mo.
                </p>
                <p className="text-center text-xs text-stone-500 dark:text-slate-500 mt-1">
                  12 Mo. {formatCurrencyLocal(baseData.basicMonat12)} · 24 Mo. {formatCurrencyLocal(baseData.basicMonat24)}
                </p>
                <AccordionItem id="basic-detail" openId={openAccordion} setOpenId={setOpenAccordion} title="Details & Zusatzseiten">
                  <p>Webaufbau mit vorgefertigter Struktur — individuell anpassbar, aber mit klaren Parametern.</p>
                  <p>Extraseite: 12 Mo. {formatCurrencyLocal(baseData.basicZusatzSeite12)} / Mo. · 24 Mo. {formatCurrencyLocal(baseData.basicZusatzSeite24)} / Mo.</p>
                  <p>Kaufoption: {formatCurrencyLocal(baseData.kaufpreisBasic)} (netto). Preise für Bestandskunden, zzgl. MwSt.</p>
                </AccordionItem>
              </div>

              {/* Individual */}
              <div className="relative rounded-2xl border border-[#8D6435]/40 dark:border-[#E6C299]/40 bg-gradient-to-b from-[#FBF6EE]/95 to-white dark:from-[#1a1612]/95 dark:to-[#0c0d11] backdrop-blur-sm p-5 flex flex-col shadow-[0_0_40px_rgba(230,194,153,0.12)] md:-translate-y-1">
                <div className="absolute top-3 right-3 text-[9px] font-black uppercase tracking-widest text-black bg-[#E6C299] px-2 py-0.5 rounded z-10">
                  Unikat
                </div>
                <h3 className="text-xl font-bold text-center text-pe-accent-text dark:text-[#E6C299] mb-2">Individual</h3>
                <p className="text-sm text-pe-muted dark:text-slate-400 text-center leading-relaxed mb-4">
                  100 % Handmade — Dein Salon als Unikat, SEO-stark und KI-konform aufgebaut.
                </p>
                <DeviceShowcase variant="individual" size="lg" />
                <ul className="mt-4 text-sm text-pe-muted dark:text-slate-400 space-y-1.5 flex-grow">
                  <li>· 100 % Handmade WordPress Unikat</li>
                  <li>· 7 Seiten inkl. Impressum &amp; Datenschutz</li>
                  <li>· + Kontakt · Trends · detaillierte Leistungen</li>
                </ul>
                <p className="mt-4 text-center text-sm font-mono text-pe-accent-text dark:text-[#E6C299]">
                  ab {formatCurrencyLocal(Math.min(baseData.individuellMonat12, baseData.individuellMonat24))} / Mo.
                </p>
                <p className="text-center text-xs text-stone-500 dark:text-slate-500 mt-1">
                  12 Mo. ab {formatCurrencyLocal(baseData.individuellMonat12)} · 24 Mo. ab {formatCurrencyLocal(baseData.individuellMonat24)}
                </p>
                <AccordionItem id="indi-detail" openId={openAccordion} setOpenId={setOpenAccordion} title="Details & Zusatzseiten">
                  <p>Handarbeit nach Deinen Wünschen — Unikat mit SEO-/KI-Konformität für maximale Sichtbarkeit.</p>
                  <p>Extraseite: 12 Mo. {formatCurrencyLocal(baseData.individuellZusatzSeite12)} / Mo. · 24 Mo. {formatCurrencyLocal(baseData.individuellZusatzSeite24)} / Mo.</p>
                  <p>Kaufoption: {formatCurrencyLocal(baseData.kaufpreisIndividual)} (netto). Preise für Bestandskunden, zzgl. MwSt.</p>
                </AccordionItem>
              </div>

              {/* Zusatz */}
              <div className="rounded-2xl border border-pe-border dark:border-slate-800 bg-white/80 dark:bg-[#0f1015]/80 backdrop-blur-sm p-5 flex flex-col hover:border-[#E6C299]/40 transition-colors">
                <h3 className="text-xl font-bold text-center text-pe-accent-text dark:text-[#E6C299] mb-3">Add-ons</h3>
                <AddonShowcase />
                <ul className="mt-4 text-sm text-pe-muted dark:text-slate-400 space-y-1.5 flex-grow">
                  <li>· SEO-Optimierung (Keywords, Tempo, Struktur)</li>
                  <li className="text-pe-accent-text dark:text-[#E6C299] font-mono text-sm">
                    SEO 12 Mo. {formatCurrencyLocal(baseData.seoMonat12)} · 24 Mo. {formatCurrencyLocal(baseData.seoMonat24)}
                  </li>
                  <li>· Logo, Visitenkarten, Flyer, Preislisten</li>
                  <li className="font-mono text-pe-muted dark:text-slate-300 text-sm">130,00 € / Std. (+ Druck/Versand)</li>
                </ul>
                <AccordionItem id="addon-detail" openId={openAccordion} setOpenId={setOpenAccordion} title="SEO & Print im Detail">
                  <p>SEO: branchenübliche Keywords, Ladezeiten, Bildbeschreibungen, klare Struktur — Exklusivpreis, zzgl. MwSt.</p>
                  <p>Print/CI: Logo & Drucksachen passend zur Website. Texte & Bilder stellst Du bereit; SEO hilft bei der Optimierung.</p>
                </AccordionItem>
              </div>
            </div>

            <div className="flex justify-center mb-10">
                <a 
                    href="https://www.friseur-coaching.de/webdesign/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-block bg-transparent hover:bg-[#E6C299]/10 text-pe-accent-text dark:text-[#E6C299] font-bold uppercase tracking-widest py-3 px-8 rounded-lg border border-[#8D6435]/50 dark:border-[#E6C299]/50 transition-colors text-sm"
                >
                    Mehr Infos zu PEWeb
                </a>
            </div>
            
        {/* Rechner — unter den Paket-Infos */}
        <div className="mb-12 w-full">
            <div className="p-6 sm:p-8 md:p-10 rounded-2xl border border-[#8D6435]/35 dark:border-[#E6C299]/35 bg-white/85 dark:bg-[#0f1015]/85 backdrop-blur-md shadow-[0_0_50px_rgba(230,194,153,0.08)] flex flex-col relative overflow-hidden">
                <div
                  className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-30"
                  style={{ background: 'radial-gradient(circle, rgba(230,194,153,0.25), transparent 70%)' }}
                />
                <div className="relative flex flex-col items-center gap-2 mb-6">
                  <img
                    src={PEWEB_LOGO}
                    alt="PEWeb"
                    className="h-28 sm:h-32 w-auto object-contain"
                  />
                  <h2 className="text-2xl sm:text-3xl font-bold text-center text-pe-text dark:text-slate-100">Berechne Dein Paket</h2>
                </div>

                <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
                  <div className="flex flex-col space-y-5">
                    {/* --- TOGGLE: Nur Kauf --- */}
                    <div className="flex justify-center lg:justify-start items-center">
                        <label htmlFor="kaufoption" className="flex items-center cursor-pointer group px-4 py-2 rounded-full border border-pe-border-strong dark:border-slate-700 bg-white/50 dark:bg-[#08090b]/50 hover:border-[#8D6435]/40 dark:hover:border-[#E6C299]/40 transition-colors">
                            <input 
                                type="checkbox" 
                                id="kaufoption" 
                                checked={isKauf} 
                                onChange={(e) => handleKaufCheck(e.target.checked)} 
                                className="w-4 h-4 mr-2 rounded border-pe-border-strong dark:border-[#2a2a2a] bg-white dark:bg-[#15171e] text-pe-accent-text dark:text-[#E6C299] focus:ring-[#E6C299] cursor-pointer"
                            />
                            <span className="text-base text-pe-muted dark:text-slate-300 group-hover:text-pe-text dark:group-hover:text-white transition-colors">Webseite kaufen (statt Leasing)</span>
                        </label>
                    </div>

                    {/* 1. Paket Wahl */}
                    <div>
                        <h3 className="text-sm font-semibold mb-3 text-pe-muted dark:text-slate-400 uppercase tracking-widest">1. Wähle Dein Grundpaket</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <button 
                                onClick={() => handlePaketChange('basic')} 
                                className={`text-center px-4 py-4 rounded-xl border transition-all ${selectedOptions.paket === 'basic' ? 'bg-stone-200 dark:bg-[#1a1d24] border-[#8D6435] dark:border-[#E6C299] ring-1 ring-[#8D6435] dark:ring-[#E6C299] text-pe-text dark:text-white shadow-[0_0_20px_rgba(230,194,153,0.15)]' : 'bg-white/80 dark:bg-[#15171e]/80 border-pe-border-strong dark:border-slate-700 text-pe-muted dark:text-slate-300 hover:border-stone-400 dark:hover:border-slate-500'}`}
                            >
                                <span className="font-bold text-base block mb-1">Basic</span>
                                <span className="text-sm text-pe-muted dark:text-slate-400">
                                    {isKauf ? calculation.btnDisplay.basic.kauf : calculation.btnDisplay.basic.leasing}
                                </span>
                            </button>
                            <button 
                                onClick={() => handlePaketChange('individual')} 
                                className={`text-center px-4 py-4 rounded-xl border transition-all ${selectedOptions.paket === 'individual' ? 'bg-stone-200 dark:bg-[#1a1d24] border-[#8D6435] dark:border-[#E6C299] ring-1 ring-[#8D6435] dark:ring-[#E6C299] text-pe-text dark:text-white shadow-[0_0_20px_rgba(230,194,153,0.15)]' : 'bg-white/80 dark:bg-[#15171e]/80 border-pe-border-strong dark:border-slate-700 text-pe-muted dark:text-slate-300 hover:border-stone-400 dark:hover:border-slate-500'}`}
                            >
                                <span className="font-bold text-base block mb-1">Individual</span>
                                <span className="text-sm text-pe-muted dark:text-slate-400">
                                    {isKauf ? calculation.btnDisplay.individual.kauf : calculation.btnDisplay.individual.leasing}
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* 2. Laufzeit (Nur anzeigen wenn NICHT Kauf) */}
                    {!isKauf && (
                        <div className={`transition-all duration-500 ease-in-out ${isStep2Enabled ? 'opacity-100' : 'opacity-40 pointer-events-none grayscale'}`}>
                            <h3 className="text-sm font-semibold mb-3 text-pe-muted dark:text-slate-400 uppercase tracking-widest">2. Wähle die Laufzeit</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <button 
                                    onClick={() => handleLaufzeitChange(12)} 
                                    className={`text-center px-4 py-3.5 rounded-xl border transition-all ${selectedOptions.laufzeit === 12 ? 'bg-stone-200 dark:bg-[#1a1d24] border-[#8D6435] dark:border-[#E6C299] ring-1 ring-[#8D6435] dark:ring-[#E6C299] text-pe-text dark:text-white' : 'bg-white/80 dark:bg-[#15171e]/80 border-pe-border-strong dark:border-slate-700 text-pe-muted dark:text-slate-300 hover:border-stone-400 dark:hover:border-slate-500'}`}
                                    disabled={!isStep2Enabled}
                                >
                                    <span className="font-bold text-base">12 Monate</span><br/>
                                    <span className="text-sm text-pe-muted dark:text-slate-400">
                                        {selectedOptions.paket ? (selectedOptions.paket === 'basic' ? calculation.btnDisplay.basic12 : calculation.btnDisplay.indi12) : '--- EUR'} / Monat
                                    </span>
                                </button>
                                <button 
                                    onClick={() => handleLaufzeitChange(24)} 
                                    className={`text-center px-4 py-3.5 rounded-xl border transition-all ${selectedOptions.laufzeit === 24 ? 'bg-stone-200 dark:bg-[#1a1d24] border-[#8D6435] dark:border-[#E6C299] ring-1 ring-[#8D6435] dark:ring-[#E6C299] text-pe-text dark:text-white' : 'bg-white/80 dark:bg-[#15171e]/80 border-pe-border-strong dark:border-slate-700 text-pe-muted dark:text-slate-300 hover:border-stone-400 dark:hover:border-slate-500'}`}
                                    disabled={!isStep2Enabled}
                                >
                                    <span className="font-bold text-base">24 Monate</span><br/>
                                    <span className="text-sm text-pe-muted dark:text-slate-400">
                                        {selectedOptions.paket ? (selectedOptions.paket === 'basic' ? calculation.btnDisplay.basic24 : calculation.btnDisplay.indi24) : '--- EUR'} / Monat
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 3. Zusatzoptionen */}
                    <div className={`transition-all duration-500 ease-in-out ${isStep3Enabled ? 'opacity-100' : 'opacity-40 pointer-events-none grayscale'}`}>
                        <h3 className="text-sm font-semibold mb-3 text-pe-muted dark:text-slate-400 uppercase tracking-widest">{isKauf ? '2.' : '3.'} Zusatzoptionen</h3>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3.5 bg-white/90 dark:bg-[#15171e]/90 rounded-xl border border-pe-border-strong dark:border-slate-700/80">
                                <PeCheck
                                    id="zusatzSeiten"
                                    checked={selectedOptions.zusatzSeitenChecked}
                                    onChange={handleZusatzSeitenCheck}
                                    disabled={!isStep3Enabled}
                                />
                                <label htmlFor="zusatzSeiten" className="flex-grow cursor-pointer text-base text-pe-text dark:text-slate-200">Zusätzliche Seiten</label>
                                
                                {selectedOptions.zusatzSeitenChecked && (
                                    <SeitenStepper
                                        value={selectedOptions.anzahlZusatzSeiten || 1}
                                        onChange={handleAnzahlZusatzSeitenChange}
                                    />
                                )}
                                <span className="text-sm text-pe-muted dark:text-slate-400 font-mono w-24 text-right">
                                    {selectedOptions.zusatzSeitenChecked && selectedOptions.anzahlZusatzSeiten > 0 
                                        ? `+ ${formatCurrencyLocal(calculation.seitenPreisDisplay)}` 
                                        : ''}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 p-3.5 bg-white/90 dark:bg-[#15171e]/90 rounded-xl border border-pe-border-strong dark:border-slate-700/80">
                                <PeCheck
                                    id="seoPaket"
                                    checked={selectedOptions.seoChecked}
                                    onChange={handleSeoCheck}
                                    disabled={!isStep3Enabled}
                                />
                                <label htmlFor="seoPaket" className="flex-grow cursor-pointer text-base text-pe-text dark:text-slate-200">SEO-Paket</label>
                                <span className="text-sm text-pe-muted dark:text-slate-400 font-mono w-24 text-right">
                                    {selectedOptions.seoChecked ? `+ ${formatCurrencyLocal(calculation.seoPreisDisplay)}` : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                  </div>

                  {/* Ergebnis + Termin */}
                  <div className="flex flex-col gap-4">
                    <div className="p-5 sm:p-6 bg-white/70 dark:bg-[#08090b]/70 border border-[#8D6435]/25 dark:border-[#E6C299]/25 rounded-xl shadow-inner flex-1">
                        <h3 className="text-lg font-bold text-center mb-4 text-pe-text dark:text-slate-100">Dein Buchungspaket</h3>
                        
                        {(selectedOptions.paket && (isKauf || selectedOptions.laufzeit)) ? (
                            <div className="space-y-4 animate-in fade-in duration-500">
                                <div className="flex justify-between items-end border-b border-pe-border dark:border-[#2a2a2a] pb-3">
                                    <div>
                                        <span className="font-semibold block text-lg text-pe-accent-text dark:text-[#E6C299]">
                                            {selectedOptions.paket === 'basic' ? 'Basic' : 'Individual'} - Paket
                                        </span>
                                        <span className="text-sm text-pe-muted dark:text-slate-400"> 
                                            {isKauf ? ' Kaufpreis (Einmalzahlung)' : ` ${selectedOptions.laufzeit} Monate Laufzeit`}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="space-y-2 text-base text-pe-muted dark:text-slate-300">
                                    <div className="flex justify-between">
                                        <span>Grundpreis:</span> 
                                        <span>{formatCurrencyLocal(calculation.grundpreisDisplay)}</span>
                                    </div>
                                    {selectedOptions.zusatzSeitenChecked && selectedOptions.anzahlZusatzSeiten > 0 && (
                                        <div className="flex justify-between">
                                            <span>Zusatzseiten ({selectedOptions.anzahlZusatzSeiten}x):</span> 
                                            <span>{formatCurrencyLocal(calculation.seitenPreisDisplay)}</span>
                                        </div>
                                    )}
                                    {selectedOptions.seoChecked && (
                                        <div className="flex justify-between">
                                            <span>SEO-Paket:</span> 
                                            <span>{formatCurrencyLocal(calculation.seoPreisDisplay)}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4 pt-4 mt-1 border-t border-pe-border dark:border-[#2a2a2a]">
                                    <div>
                                        <label className="text-xs text-pe-muted dark:text-slate-400 block mb-1.5 text-center uppercase tracking-wider">
                                            {isKauf ? 'Gesamtpreis (Netto)' : 'Netto / Monat'}
                                        </label>
                                        <CalculatedField value={calculation.netto} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-pe-muted dark:text-slate-400 block mb-1.5 text-center uppercase tracking-wider">
                                            {isKauf ? 'Gesamtpreis (Brutto)' : 'Brutto / Monat'}
                                        </label>
                                        <CalculatedField value={calculation.brutto} />
                                    </div>
                                </div>
                                
                                <div className="pt-1 text-center">
                                    <p className="text-xs text-stone-500 dark:text-slate-500">(Alle Bruttopreise inkl. gesetzl. MwSt.)</p>
                                </div>
                            </div>
                        ) : (
                            <div className="py-8 text-center space-y-2 opacity-50">
                                <p className="text-base text-stone-500 dark:text-slate-500 italic">Bitte wähle links Dein Paket {isKauf ? '' : 'und die Laufzeit '}aus.</p>
                                <div className="h-1 w-1/3 bg-stone-300 dark:bg-[#2a2a2a] rounded mx-auto"></div>
                                <div className="h-1 w-1/2 bg-stone-300 dark:bg-[#2a2a2a] rounded mx-auto"></div>
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border border-pe-border dark:border-slate-800 bg-white/60 dark:bg-[#0f1015]/60 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
                      <p className="text-base text-pe-muted dark:text-slate-300 font-semibold">
                        Interesse? Unverbindlich sprechen.
                      </p>
                      <a
                        href="https://calendly.com/friseurcoaching"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block shrink-0 bg-[#E6C299] text-black font-bold py-3 px-7 rounded-lg hover:bg-[#d9b183] dark:hover:bg-white transition-colors shadow-[0_0_24px_rgba(230,194,153,0.25)] text-sm uppercase tracking-wider"
                      >
                        Termin buchen
                      </a>
                    </div>
                  </div>
                </div>
            </div>
        </div>

            {/* Kleingedruckt — Accordions */}
            <div className="max-w-3xl mx-auto space-y-2">
              <p className="text-xs font-bold uppercase tracking-widest text-stone-500 dark:text-slate-600 mb-2 text-center">
                Konditionen & Rechtliches
              </p>
              <AccordionItem id="vertrag" openId={openAccordion} setOpenId={setOpenAccordion} title="PEWeb Verträge & Laufzeit">
                <p>Die PEWeb-Verträge haben eine Mindestlaufzeit — vorzeitige Kündigung nur in Ausnahmefällen. Deine Laufzeit bestimmst Du selbst. Nach der Regellaufzeit verlängert sich der Vertrag auf unbestimmte Zeit und ist dann monatlich kündbar.</p>
              </AccordionItem>
              <AccordionItem id="leasing" openId={openAccordion} setOpenId={setOpenAccordion} title="Leasing vs. Kauf">
                <p><strong className="text-pe-muted dark:text-slate-300">Wichtig:</strong> Wie beim Leasing eines Autos bleibt die Website in unserem Besitz. Erst nach der doppelten Laufzeit oder der anteiligen Bezahlung der Schlussrate kann die Website in Deinen Besitz übergehen. Kaufoptionen kannst Du im Rechner darüber aktivieren.</p>
              </AccordionItem>
              <AccordionItem id="pflege" openId={openAccordion} setOpenId={setOpenAccordion} title="Pflegevertrag & Updates">
                <p>Nach Ablauf kannst Du den Pflegevertrag flexibel verlängern (ohne Kündigungsfrist) oder die Seite mit einem neuen Vertrag auffrischen bzw. neugestalten. Wir setzen auf langjährige Zusammenarbeit und aktualisieren Deine Website regelmäßig.</p>
                <p>Keine versteckten Kosten und keine Knebelverträge — Du behältst alle Freiheiten.</p>
              </AccordionItem>
            </div>
        </div>
      </div>
    </div>
  );
};

export default WebDesignPage;
